/**
 * Displays the user's current subscription. Loads from Firestore (subscriptions by userId).
 * When revalidateOnLoad is true, loads via getSubscriptionsByEmail with revalidate so the store is re-checked and a verified badge can be shown.
 * Can also show a just-purchased subscription via lastPurchasedSubscription before Firestore has synced.
 * Requires userId from localStorage (loggedInUser); if missing, prompts sign-in. See docs/PAYMENTS.md.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { firebaseApp } from '../utils/firebaseConfig';
import { FIREBASE_FUNCTIONS } from '../utils/firebaseFunctions';

const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);

const LOG_PREFIX = '[Subscriptions]';

const PRODUCT_ID_TO_TITLE_KEY: Record<string, string> = {
  'com.kol_torah.parasha_1': 'payments_plan_parasha_title_1',
  'com.kol_torah.parasha_2': 'payments_plan_parasha_title_2',
};

const PRODUCT_ID_TO_DESC_KEY: Record<string, string> = {
  'com.kol_torah.parasha_1': 'payments_plan_parasha_desc',
  'com.kol_torah.parasha_2': 'payments_plan_any_desc',
};

export interface SubscriptionDoc {
  id: string;
  productId: string;
  userId: string | null;
  expiresAt: Date | string | { toDate: () => Date } | { seconds: number };
  validatedAt?: unknown;
  isDemo?: boolean;
  cancelAtPeriodEnd?: boolean;
  cancelledAt?: string | null;
}

/** Passed from PaymentsPage after a successful purchase so the section can show the new subscription immediately */
export interface LastPurchasedSubscription {
  productId: string;
  expiresAt?: string;
}

function parseExpiresAt(expiresAt: SubscriptionDoc['expiresAt']): Date {
  if (expiresAt instanceof Date) return expiresAt;
  if (typeof expiresAt === 'string') return new Date(expiresAt);
  if (expiresAt && typeof (expiresAt as { toDate?: () => Date }).toDate === 'function') {
    return (expiresAt as { toDate: () => Date }).toDate();
  }
  if (expiresAt && typeof expiresAt === 'object' && 'seconds' in (expiresAt as object)) {
    return new Date((expiresAt as unknown as { seconds: number }).seconds * 1000);
  }
  return new Date(0);
}

interface CurrentSubscriptionSectionProps {
  /** When set, show this subscription in the section (e.g. right after successful purchase) */
  lastPurchasedSubscription?: LastPurchasedSubscription | null;
  /** Called when subscriptions have been loaded from the server (so parent can clear lastPurchasedSubscription) */
  onSubscriptionLoaded?: () => void;
  /** When true, load subscriptions via API with revalidate so the app store is re-checked and a verified badge is shown */
  revalidateOnLoad?: boolean;
}

const CurrentSubscriptionSection: React.FC<CurrentSubscriptionSectionProps> = ({
  lastPurchasedSubscription = null,
  onSubscriptionLoaded,
  revalidateOnLoad = false,
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.startsWith('he') ?? false;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionDoc | null>(null);
  const [validatedWithStore, setValidatedWithStore] = useState(false);
  const [restoringFromFlutter] = useState(false);
  const onSubscriptionLoadedRef = useRef(onSubscriptionLoaded);
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    const apply = (u: string | null, e: string | null, source: string) => {
      console.log(LOG_PREFIX, 'CurrentSubscriptionSection identity', {
        source,
        hasUserId: !!u,
        userIdLength: u?.length ?? 0,
        hasEmail: !!e,
        emailPrefix: e ? `${e.slice(0, 3)}***` : null,
      });
      setUserId(u);
      setEmail(e);
    };
    const storedUser = localStorage.getItem('loggedInUser');
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser) as { userId?: string; uid?: string; email?: string };
        apply(
          parsed.userId ?? parsed.uid ?? parsed.email ?? null,
          parsed.email?.trim() ? String(parsed.email).trim().toLowerCase() : null,
          'localStorage',
        );
        return;
      } catch {
        /* fall through to auth */
      }
    }
    console.log(
      LOG_PREFIX,
      'CurrentSubscriptionSection: no localStorage, waiting for Firebase Auth',
    );
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user)
        apply(
          user.uid,
          user.email?.trim() ? user.email.trim().toLowerCase() : null,
          'Firebase Auth',
        );
      else apply(null, null, 'Firebase Auth (signed out)');
    });
    return () => unsubscribe();
  }, []);

  onSubscriptionLoadedRef.current = onSubscriptionLoaded;

  useEffect(() => {
    if (!userId && !email) {
      setLoading(false);
      setSubscription(null);
      setValidatedWithStore(false);
      return;
    }

    const loadSubscriptions = async () => {
      setLoading(true);
      setValidatedWithStore(false);
      const requestPayload = {
        userId: userId ?? undefined,
        email: email ?? undefined,
        revalidate: true,
      };
      console.log(LOG_PREFIX, 'loadSubscriptions start', {
        revalidateOnLoad,
        hasUserId: !!userId,
        hasEmail: !!email,
      });
      try {
        if (revalidateOnLoad) {
          const url = FIREBASE_FUNCTIONS.getSubscriptionsByEmail();
          console.log(LOG_PREFIX, 'getSubscriptionsByEmail request', {
            url: url.slice(0, 50) + '...',
            body: requestPayload,
          });
          const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestPayload),
          });
          const json = (await res.json()) as {
            success?: boolean;
            subscriptions?: Array<{
              id: string;
              productId: string;
              userId: string | null;
              expiresAt: string | null;
              isDemo?: boolean;
              validatedWithStore?: boolean;
              cancelAtPeriodEnd?: boolean;
              cancelledAt?: string | null;
            }>;
            error?: string;
          };
          console.log(LOG_PREFIX, 'getSubscriptionsByEmail response', {
            ok: res.ok,
            status: res.status,
            success: json.success,
            subscriptionCount: json.subscriptions?.length ?? 0,
            error: json.error,
          });
          if (!res.ok || !json.success || !Array.isArray(json.subscriptions)) {
            console.warn(LOG_PREFIX, 'API error or no subscriptions', json);
            setSubscription(null);
            return;
          }
          const first = json.subscriptions[0];
          if (first) {
            console.log(LOG_PREFIX, 'getSubscriptionsByEmail using first', {
              id: first.id,
              productId: first.productId,
              expiresAt: first.expiresAt,
            });
            setSubscription({
              id: first.id,
              productId: first.productId,
              userId: first.userId,
              expiresAt: first.expiresAt ?? new Date(0),
              isDemo: first.isDemo,
              cancelAtPeriodEnd: first.cancelAtPeriodEnd,
              cancelledAt: first.cancelledAt ?? null,
            });
            setValidatedWithStore(first.validatedWithStore === true);
            onSubscriptionLoadedRef.current?.();
          } else {
            console.log(LOG_PREFIX, 'getSubscriptionsByEmail: subscriptions array empty');
            setSubscription(null);
          }
        } else {
          const subsRef = collection(db, 'subscriptions');
          const docs: SubscriptionDoc[] = [];
          let countUserId = 0;
          let countEmail = 0;
          if (userId) {
            const qUserId = query(subsRef, where('userId', '==', userId), limit(10));
            const snapUserId = await getDocs(qUserId);
            countUserId = snapUserId.docs.length;
            docs.push(
              ...snapUserId.docs.map((d) => ({ id: d.id, ...d.data() }) as SubscriptionDoc),
            );
          }
          if (email) {
            const emailLower = email.toLowerCase();
            const qEmail = query(subsRef, where('email', '==', emailLower), limit(10));
            const snapEmail = await getDocs(qEmail);
            countEmail = snapEmail.docs.length;
            for (const d of snapEmail.docs) {
              const doc = { id: d.id, ...d.data() } as SubscriptionDoc;
              if (!docs.some((x) => x.id === doc.id)) docs.push(doc);
            }
          }
          console.log(LOG_PREFIX, 'Firestore direct query result', {
            countByUserId: countUserId,
            countByEmail: countEmail,
            mergedCount: docs.length,
          });
          const sorted = docs.sort((a, b) => {
            const aVal = a.validatedAt;
            const bVal = b.validatedAt;
            const aTime =
              aVal && typeof (aVal as { toMillis?: () => number }).toMillis === 'function'
                ? (aVal as { toMillis: () => number }).toMillis()
                : aVal && typeof (aVal as { seconds?: number }).seconds === 'number'
                  ? (aVal as { seconds: number }).seconds * 1000
                  : 0;
            const bTime =
              bVal && typeof (bVal as { toMillis?: () => number }).toMillis === 'function'
                ? (bVal as { toMillis: () => number }).toMillis()
                : bVal && typeof (bVal as { seconds?: number }).seconds === 'number'
                  ? (bVal as { seconds: number }).seconds * 1000
                  : 0;
            return bTime - aTime;
          });
          const doc = sorted[0];
          if (doc) {
            console.log(LOG_PREFIX, 'Firestore: using subscription', {
              id: doc.id,
              productId: doc.productId,
            });
            setSubscription(doc);
            onSubscriptionLoadedRef.current?.();
          } else {
            console.log(LOG_PREFIX, 'Firestore: no subscription doc after merge');
            setSubscription(null);
          }
        }
      } catch (e) {
        console.warn(LOG_PREFIX, 'Load error', e);
        setSubscription(null);
      } finally {
        setLoading(false);
      }
    };

    void loadSubscriptions();
  }, [userId, email, revalidateOnLoad, onSubscriptionLoaded]);

  if (loading) {
    return (
      <section
        className="current-subscription-section"
        style={{
          marginBottom: 24,
          padding: 20,
          borderRadius: 12,
          background: '#F9FAFB',
          border: '1px solid #E5E7EB',
          direction: isRtl ? 'rtl' : 'ltr',
        }}
      >
        <h3 style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 600, color: '#374151' }}>
          {t('subscription_details_title', 'Your subscription')}
        </h3>
        <p style={{ margin: 0, fontSize: 14, color: '#6B7280' }}>
          {t('subscription_details_loading', 'Loading...')}
        </p>
      </section>
    );
  }

  if (!userId) {
    // In the app WebView, try restoring Firebase Auth from Flutter before showing Sign in
    if (restoringFromFlutter) {
      return (
        <section
          className="current-subscription-section"
          style={{
            marginBottom: 24,
            padding: 20,
            borderRadius: 12,
            background: '#F9FAFB',
            border: '1px solid #E5E7EB',
            direction: isRtl ? 'rtl' : 'ltr',
          }}
        >
          <h3 style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 600, color: '#374151' }}>
            {t('subscription_details_title', 'Your subscription')}
          </h3>
          <p style={{ margin: 0, fontSize: 14, color: '#6B7280' }}>
            {t('subscription_details_loading', 'Loading...')}
          </p>
        </section>
      );
    }
    return (
      <section
        className="current-subscription-section"
        style={{
          marginBottom: 24,
          padding: 20,
          borderRadius: 12,
          background: '#F9FAFB',
          border: '1px solid #E5E7EB',
          direction: isRtl ? 'rtl' : 'ltr',
        }}
      >
        <h3 style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 600, color: '#374151' }}>
          {t('subscription_details_title', 'Your subscription')}
        </h3>
        <p style={{ margin: '0 0 12px 0', fontSize: 14, color: '#6B7280' }}>
          {t('subscription_details_sign_in', 'Sign in to see your subscription.')}
        </p>
        <button
          type="button"
          onClick={() => navigate('/login')}
          style={{
            padding: '10px 20px',
            fontSize: 15,
            fontWeight: 600,
            color: '#fff',
            background: '#3B7BF4',
            border: 'none',
            borderRadius: 10,
            cursor: 'pointer',
          }}
        >
          {t('subscription_details_sign_in_button', 'Sign in')}
        </button>
      </section>
    );
  }

  // Prefer showing the just-purchased subscription so it appears in the section immediately
  const displaySubscription: SubscriptionDoc | null =
    lastPurchasedSubscription != null
      ? {
          id: 'just-purchased',
          productId: lastPurchasedSubscription.productId,
          userId: userId,
          expiresAt: lastPurchasedSubscription.expiresAt
            ? new Date(lastPurchasedSubscription.expiresAt)
            : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // default 1 year if missing
        }
      : subscription;

  if (!displaySubscription) {
    return (
      <></>
      // <section
      //   className="current-subscription-section"
      //   style={{
      //     marginBottom: 24,
      //     padding: 20,
      //     borderRadius: 12,
      //     background: "#FFFBEB",
      //     border: "1px solid #FCD34D",
      //     direction: isRtl ? "rtl" : "ltr",
      //   }}
      // >
      //   <h3 style={{ margin: "0 0 8px 0", fontSize: 16, fontWeight: 600, color: "#374151" }}>
      //     {t("subscription_details_title", "Your subscription")}
      //   </h3>
      //   <p style={{ margin: "0 0 4px 0", fontSize: 14, color: "#6B7280" }}>
      //     {t("subscription_details_none", "You don't have an active subscription.")}
      //   </p>
      //   <p style={{ margin: "8px 0 0 0", fontSize: 13, color: "#92400E", lineHeight: 1.4 }}>
      //     {t(
      //       "subscription_details_purchased_elsewhere",
      //       "If you already paid in the app, sign in with the same account you used to purchase. If the problem continues, contact support."
      //     )}
      //   </p>
      // </section>
    );
  }

  const expiresAt = parseExpiresAt(displaySubscription.expiresAt);
  const isActive = expiresAt > new Date();
  const cancelAtPeriodEnd = displaySubscription.cancelAtPeriodEnd === true;
  const planTitleKey =
    PRODUCT_ID_TO_TITLE_KEY[displaySubscription.productId] ?? 'subscription_title';
  const planDescKey =
    PRODUCT_ID_TO_DESC_KEY[displaySubscription.productId] ?? 'subscription_details_plan_desc';
  const planTitle = t(planTitleKey);
  const planDescription = t(planDescKey);
  const expiresLabel = isActive
    ? t('subscription_details_expires', 'Expires')
    : t('subscription_details_expired', 'Expired');
  const isJustPurchased = lastPurchasedSubscription != null;
  const showVerifiedBadge = validatedWithStore || isJustPurchased;

  return (
    <section
      className="current-subscription-section"
      style={{
        marginBottom: 24,
        padding: 20,
        borderRadius: 12,
        background: isActive ? '#ECFDF5' : '#FEF2F2',
        border: isActive ? '1px solid #A7F3D0' : '1px solid #FECACA',
        direction: isRtl ? 'rtl' : 'ltr',
      }}
    >
      <h3 style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 600, color: '#374151' }}>
        {t('subscription_details_title', 'Your subscription')}
      </h3>
      <p style={{ margin: '0 0 4px 0', fontSize: 12, color: '#6B7280' }}>
        {t('subscription_details_you_have', 'What you have:')}
      </p>
      <p
        style={{
          margin: '0 0 4px 0',
          fontSize: 15,
          fontWeight: 600,
          color: '#1F2937',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {planTitle}
        {showVerifiedBadge && (
          <span
            title={String(t('subscription_details_verified_with_store', 'Verified with app store'))}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: '#059669',
              color: '#fff',
              fontSize: 12,
              fontWeight: 700,
            }}
            aria-label={String(
              t('subscription_details_verified_with_store', 'Verified with app store'),
            )}
          >
            ✓
          </span>
        )}
      </p>
      <p style={{ margin: '0 0 8px 0', fontSize: 14, color: '#4B5563', lineHeight: 1.4 }}>
        {planDescription}
      </p>
      <p style={{ margin: 0, fontSize: 14, color: '#6B7280' }}>
        {isActive ? (
          <>
            <span style={{ fontWeight: 500, color: '#059669' }}>
              {cancelAtPeriodEnd
                ? t('subscription_details_status_cancelling', 'Cancelling')
                : t('subscription_details_status_active', 'Active')}
            </span>
            {lastPurchasedSubscription?.expiresAt ? (
              <>
                {' · '}
                {cancelAtPeriodEnd
                  ? t('subscription_details_cancels_on', 'Cancels on')
                  : expiresLabel}
                : {expiresAt.toLocaleDateString(undefined, { dateStyle: 'medium' })}
              </>
            ) : isJustPurchased ? (
              <> · {t('subscription_details_just_activated', 'Just activated')}</>
            ) : (
              <>
                {' · '}
                {cancelAtPeriodEnd
                  ? t('subscription_details_cancels_on', 'Cancels on')
                  : expiresLabel}
                : {expiresAt.toLocaleDateString(undefined, { dateStyle: 'medium' })}
              </>
            )}
          </>
        ) : (
          <>
            <span style={{ fontWeight: 500, color: '#DC2626' }}>
              {t('subscription_details_status_expired', 'Expired')}
            </span>
            {' · '}
            {expiresLabel}: {expiresAt.toLocaleDateString(undefined, { dateStyle: 'medium' })}
          </>
        )}
      </p>
      {'isDemo' in displaySubscription && displaySubscription.isDemo && (
        <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#9CA3AF' }}>
          {t('subscription_details_demo', 'Demo subscription (testing only)')}
        </p>
      )}
    </section>
  );
};

export default CurrentSubscriptionSection;
